import React from 'react'
import { Box } from '@mui/material'
import Slider, { SliderTypeMap } from '@mui/material/Slider'
import Typography from '@mui/material/Typography'
import clsx from 'clsx'

import FTGTooltip from '@core/components/FTGTooltip'

type FTGSliderProps = {
  title?: string
  disabled?: boolean
  notSupported?: boolean
  value?: number
  onChange: (event: Event, value: number | number[], activeThumb: number) => void
  markPosition?: 'bottom' | 'top'
  marks?: SliderTypeMap['props']['marks']
  customStyles?: SliderTypeMap['props']['sx']
  v2Style?: boolean
}

const styles = {
  container: {
    '& .position-top': {
      '& .MuiSlider-root': {
        mb: 0,
        mt: 2.5,
      },
      '& .MuiSlider-markLabel': {
        top: '-30px',
      },
    },
  },
  v2Style: {
    '& .MuiSlider-markLabel[data-index="0"]': {
      transform: 'translateX(0)',
    },
    '& .MuiSlider-markLabel[data-index="2"]': {
      transform: 'translateX(-100%)',
    },
    '& .MuiSlider-thumb': {
      width: 24,
      height: 24,
    },
    '& .MuiSlider-markLabel': {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.57,
    },
  },
}
const lowValue = 0
const mediumValue = 50
const highValue = 100
const defaultMarks = [
  {
    value: lowValue,
    label: 'Low',
  },
  {
    value: mediumValue,
    label: 'Medium',
  },
  {
    value: highValue,
    label: 'High',
  },
]

const FTGSlider = React.forwardRef<HTMLInputElement, FTGSliderProps>(
  (
    {
      title,
      disabled,
      onChange,
      value = mediumValue,
      notSupported,
      marks = defaultMarks,
      markPosition = 'bottom',
      customStyles = {},
      v2Style = false,
    },
    ref,
  ) => {
    const inputLabel = `${title} slider`

    return (
      <Box
        aria-label="ftg-slider"
        sx={{ ...styles.container, ...(v2Style ? styles.v2Style : {}), ...customStyles }}
      >
        {title && (
          <Typography
            variant="h5"
            gutterBottom
            id={inputLabel}
            sx={{ opacity: disabled || notSupported ? 0.5 : 1 }}
          >
            {title}
          </Typography>
        )}
        <FTGTooltip
          data-testid="ftg-slider-tooltip"
          title={
            notSupported ? 'The model you have selected does not support creativity levels' : ''
          }
          placement="left"
          leaveDelay={200}
        >
          <div
            className={clsx({
              'position-top': markPosition === 'top',
            })}
          >
            <Slider
              value={value}
              aria-labelledby={inputLabel}
              step={null}
              valueLabelDisplay="off"
              marks={marks || defaultMarks}
              color="secondary"
              disabled={disabled || notSupported}
              min={lowValue}
              max={highValue}
              onChange={onChange}
              slotProps={{
                input: {
                  ref,
                  // @ts-ignore
                  'data-testid': 'ftg-slider-input',
                },
              }}
            />
          </div>
        </FTGTooltip>
      </Box>
    )
  },
)

FTGSlider.displayName = 'FTGSlider'

export default FTGSlider
